import { PlusIcon, XIcon } from '@primer/octicons-react';
import { FieldArray, getIn, useField, useFormikContext } from 'formik';
import { set } from 'lodash-es';
import React from 'react';
import { graphql } from 'react-relay';

import { BrandMessageTemplateCreateDialog_brandMessageTemplateCreateMutation } from '../../../relay/__generated__/BrandMessageTemplateCreateDialog_brandMessageTemplateCreateMutation.graphql';
import Button from '../../core/Button';
import Checkbox from '../../core/Checkbox';
import Dialog, { DialogProps } from '../../core/Dialog';
import FormLayout from '../../core/FormLayout';
import Grid from '../../core/Grid';
import IconButton from '../../core/IconButton';
import MutationFormik, { MutationFormikProps } from '../../core/MutationFormik';
import NonFieldError from '../../core/NonFieldError';
import NumberField from '../../core/NumberField';
import SelectField from '../../core/SelectField';
import Text from '../../core/Text';
import TextField from '../../core/TextField';
import TextareaField from '../../core/TextareaField';
import UploadField from '../../core/UploadField';
import View from '../../core/View';
import BrandMessageTemplatePreview from '../BrandMessageTemplatePreview';
import {
  BRAND_MESSAGE_LIMITS,
  BRAND_MESSAGE_TYPE_OPTIONS,
  BRAND_MESSAGE_VARIABLE_RULES,
  BrandMessageType,
  CAROUSEL_ITEM_LIMITS,
  COMMERCE_ADDITIONAL_CONTENT_NEWLINE_MAX,
  COMMERCE_PRICE_VARIABLE_FIELDS,
  WIDE_ITEM_LIST_LIMITS,
  countNewlines,
  getBrandMessageLimit,
  getButtonMax,
} from '../constants';

type Props = {} & DialogProps &
  Partial<
    Pick<
      MutationFormikProps<BrandMessageTemplateCreateDialog_brandMessageTemplateCreateMutation>,
      'config' | 'initialValues'
    >
  >;

export type BrandMessageTemplateFormValues = Record<string, any>;
type FormValues = BrandMessageTemplateFormValues;

const emptyUpload = () => ({ key: '', size: 0 });
const emptyButton = () => ({ name: '', linkType: 'WL', linkMobile: '' });
const emptyWideItem = () => ({ title: '', image: emptyUpload(), imageUrl: null, linkMobile: '' });
const emptyCarouselItem = (type: BrandMessageType) => ({
  header: '',
  content: '',
  image: emptyUpload(),
  imageUrl: null,
  imageLink: '',
  additionalContent: '',
  commerce:
    type === 'CAROUSEL_COMMERCE'
      ? {
          title: '',
          regularPrice: null,
          discountPrice: null,
          discountRate: null,
          discountFixed: null,
          usePriceVariable: false,
        }
      : null,
  buttons: [emptyButton()],
  coupon: null,
});

export const initialValuesForType = (type: BrandMessageType) => ({
  content: '',
  image: ['IMAGE', 'WIDE', 'COMMERCE'].includes(type) ? emptyUpload() : null,
  imageUrl: null,
  imageLink: null,
  header: '',
  additionalContent: '',
  mainWideItem: type === 'WIDE_ITEM_LIST' ? emptyWideItem() : null,
  subWideItemList: type === 'WIDE_ITEM_LIST' ? [emptyWideItem(), emptyWideItem()] : null,
  video: type === 'PREMIUM_VIDEO' ? emptyUpload() : null,
  videoId: null,
  commerce:
    type === 'COMMERCE'
      ? {
          title: '',
          regularPrice: null,
          discountPrice: null,
          discountRate: null,
          discountFixed: null,
          usePriceVariable: false,
        }
      : null,
  carousel:
    type === 'CAROUSEL_FEED' || type === 'CAROUSEL_COMMERCE'
      ? { head: null, list: [emptyCarouselItem(type), emptyCarouselItem(type)], tail: null }
      : null,
  buttons: type === 'COMMERCE' ? [emptyButton()] : [],
  coupon: null,
});

const hasImage = (item?: { image?: { key?: string } | null; imageUrl?: string | null } | null) =>
  Boolean(item?.image?.key || item?.imageUrl);

/**
 * 탭(캐러셀 카드 / 리스트 서브)은 활성 탭만 화면에 있어 formik 의 필드 단위 required 로는
 * 보이지 않는 탭의 빈 값을 잡지 못한다. 그대로 두면 GraphQL 이
 * "input.subWideItemList[1].image.key ... not to be None" 같은 원문 오류를 뱉는다.
 * → 제출 전에 전 탭을 훑어 어디가 비었는지 한국어로 알려준다.
 */
export const validateBrandMessageTemplate = (values: FormValues) => {
  const type = values.chatBubbleType as BrandMessageType;
  const errors: FormValues = {};
  const put = (path: string, message: string) => set(errors, path, message);

  if (type === 'CAROUSEL_FEED' || type === 'CAROUSEL_COMMERCE') {
    const isCommerce = type === 'CAROUSEL_COMMERCE';
    (values.carousel?.list ?? []).forEach((item: FormValues, index: number) => {
      const at = `carousel.list[${index}]`;
      if (!hasImage(item)) put(`${at}.image`, '이미지를 등록해 주세요');
      if (isCommerce) {
        if (!(item.commerce?.title || '').trim()) put(`${at}.commerce.title`, '상품명을 입력해 주세요');
      } else {
        if (!(item.header || '').trim()) put(`${at}.header`, '제목을 입력해 주세요');
        if (!(item.content || '').trim()) put(`${at}.content`, '내용을 입력해 주세요');
      }
      if (!(item.buttons ?? []).some((button: FormValues) => (button?.name || '').trim())) {
        put(`${at}.buttons[0].name`, '버튼명을 입력해 주세요');
      }
    });
    if (values.carousel?.head && !hasImage(values.carousel.head)) {
      put('carousel.head.image', '이미지를 등록해 주세요');
    }
  }

  if (type === 'WIDE_ITEM_LIST') {
    if (!hasImage(values.mainWideItem)) put('mainWideItem.image', '이미지를 등록해 주세요');
    (values.subWideItemList ?? []).forEach((item: FormValues, index: number) => {
      const at = `subWideItemList[${index}]`;
      if (!hasImage(item)) put(`${at}.image`, '이미지를 등록해 주세요');
      if (!(item.title || '').trim()) put(`${at}.title`, '타이틀을 입력해 주세요');
      if (!(item.linkMobile || '').trim()) put(`${at}.linkMobile`, '링크를 입력해 주세요');
    });
  }

  return errors;
};

/** 탭 안(캐러셀 카드 / 리스트 서브)에 오류가 있는지 — 탭 마커용 */
export const hasTabError = (errors: FormValues, path: string) => Boolean(getIn(errors, path));

const numberFields = new Set(['regularPrice', 'discountPrice', 'discountRate', 'discountFixed']);
export const normalizeBrandMessageTemplateInput = (value: any, key?: string): any => {
  if (value === '') return null;
  if (Array.isArray(value)) return value.map((item) => normalizeBrandMessageTemplateInput(item));
  if (value && typeof value === 'object')
    return Object.fromEntries(
      Object.entries(value).map(([itemKey, item]) => [itemKey, normalizeBrandMessageTemplateInput(item, itemKey)]),
    );
  if (key && numberFields.has(key) && value != null) return Number(value);
  return value;
};

type BrandImageSpecKey = 'wide' | 'image' | 'carousel' | 'commerce' | 'listMain' | 'listSub';

type BrandImageSpec = {
  minWidth: number;
  minRatio?: number;
  maxRatio?: number;
  fixedRatio?: readonly [number, number];
  description: string;
};

export const BRAND_IMAGE_SPECS: Record<BrandImageSpecKey, BrandImageSpec> = {
  wide: {
    minWidth: 500,
    minRatio: 1,
    maxRatio: 2,
    description: '권장 800×600px · 가로 500px 이상 · 비율 2:1 ~ 1:1',
  },
  image: {
    minWidth: 500,
    minRatio: 0.75,
    maxRatio: 2,
    description: '권장 800×400px · 가로 500px 이상 · 비율 2:1 ~ 3:4',
  },
  carousel: {
    minWidth: 500,
    minRatio: 0.75,
    maxRatio: 2,
    description: '모든 카드 이미지 동일 비율 권장 · 가로 500px 이상 · 비율 2:1 ~ 3:4',
  },
  commerce: {
    minWidth: 500,
    minRatio: 0.75,
    maxRatio: 2,
    description: '권장 800×400px 또는 800×600px · 가로 500px 이상 · 비율 2:1 ~ 3:4',
  },
  listMain: {
    minWidth: 500,
    fixedRatio: [2, 1],
    description: '권장 800×400px · 가로 500px 이상 · 고정 비율 2:1',
  },
  listSub: {
    minWidth: 500,
    fixedRatio: [1, 1],
    description: '가로 500px 이상 · 고정 비율 1:1',
  },
};

export const getBrandImageIssue = (width: number, height: number, specKey: BrandImageSpecKey) => {
  if (!width || !height) return null;
  const spec = BRAND_IMAGE_SPECS[specKey];
  const issues: string[] = [];
  if (width < spec.minWidth) issues.push(`가로 ${spec.minWidth}px 이상 필요`);

  const ratio = width / height;
  if (spec.fixedRatio) {
    const [ratioWidth, ratioHeight] = spec.fixedRatio;
    const targetRatio = ratioWidth / ratioHeight;
    if (Math.abs(ratio - targetRatio) / targetRatio > 0.02) issues.push(`고정 비율 ${ratioWidth}:${ratioHeight} 필요`);
  } else {
    if (spec.minRatio && ratio < spec.minRatio) issues.push(`최소 비율 ${spec.minRatio}:1 필요`);
    if (spec.maxRatio && ratio > spec.maxRatio) issues.push(`최대 비율 ${spec.maxRatio}:1 필요`);
  }

  return issues.length ? `현재 ${width}×${height} — ${issues.join(' / ')} (${spec.description})` : null;
};

type ImageValidation = {
  source: string;
  width?: number;
  height?: number;
  issue?: string;
};

const BrandImageUploadField = ({
  label,
  name,
  required,
  specKey,
}: {
  label: string;
  name: string;
  required: boolean;
  specKey: BrandImageSpecKey;
}) => {
  const [{ value }] = useField<{ objectUrl?: string | null } | null>(name);
  const { setFieldError } = useFormikContext<FormValues>();
  const source = value?.objectUrl || '';
  const [validation, setValidation] = React.useState<ImageValidation | null>(null);
  const currentValidation = validation?.source === source ? validation : null;
  const validationError = source
    ? currentValidation
      ? currentValidation.issue
      : '이미지 규격을 확인하고 있습니다.'
    : undefined;

  React.useEffect(() => {
    if (!source) {
      setValidation(null);
      return;
    }

    let cancelled = false;
    const image = new window.Image();
    setValidation({ source });
    setFieldError(name, '이미지 규격을 확인하고 있습니다.');
    image.onload = () => {
      if (cancelled) return;
      const width = image.naturalWidth;
      const height = image.naturalHeight;
      const issue = getBrandImageIssue(width, height, specKey) || undefined;
      setValidation({ source, width, height, issue });
      setFieldError(name, issue);
    };
    image.onerror = () => {
      if (cancelled) return;
      const issue = '이미지 크기를 확인할 수 없습니다. 다시 첨부해 주세요.';
      setValidation({ source, issue });
      setFieldError(name, issue);
    };
    image.src = source;

    return () => {
      cancelled = true;
    };
  }, [name, setFieldError, source, specKey]);

  return (
    <View>
      <UploadField
        label={label}
        name={name}
        required={required}
        accept={'image/jpeg,image/png'}
        caption={BRAND_IMAGE_SPECS[specKey].description}
        validate={() => validationError}
      />
      {currentValidation?.width && currentValidation.height && !currentValidation.issue ? (
        <Text display={'block'} mt={1} color={'success.fg'} fontSize={0}>
          규격 확인 완료 · {currentValidation.width}×{currentValidation.height}px
        </Text>
      ) : null}
    </View>
  );
};

type EditorTab = { id: string; label: string; accent?: boolean; error?: boolean };

const EditorTabs = ({
  tabs,
  activeId,
  onSelect,
  children,
}: React.PropsWithChildren<{
  tabs: EditorTab[];
  activeId: string;
  onSelect: (id: string) => void;
}>) => (
  <View
    mb={3}
    sx={{
      display: 'flex',
      alignItems: 'flex-end',
      flexWrap: 'wrap',
      borderBottom: '1px solid',
      borderColor: 'border.default',
    }}
    role={'tablist'}
  >
    {tabs.map((tab) => {
      const active = tab.id === activeId;
      const activeColor = tab.accent ? '#0969da' : '#24292f';
      return (
        <button
          key={tab.id}
          type={'button'}
          role={'tab'}
          aria-selected={active}
          onClick={() => onSelect(tab.id)}
          style={{
            padding: '7px 14px',
            border: `1px solid ${tab.accent ? '#0969da' : '#d0d7de'}`,
            borderBottom: active ? `2px solid ${activeColor}` : '1px solid #d0d7de',
            background: active ? activeColor : '#fff',
            color: active ? '#fff' : tab.accent ? '#0969da' : '#24292f',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            marginRight: -1,
          }}
        >
          {tab.label}
          {tab.error ? <span style={{ color: '#cf222e', marginLeft: 4 }}>*</span> : null}
        </button>
      );
    })}
    {children}
  </View>
);

const AddTabButton = ({ label, onClick, accent = false }: { label: string; onClick: () => void; accent?: boolean }) => (
  <button
    type={'button'}
    onClick={onClick}
    style={{
      padding: '7px 12px',
      border: `1px dashed ${accent ? '#0969da' : '#d0d7de'}`,
      background: '#fff',
      color: accent ? '#0969da' : '#57606a',
      fontSize: 12,
      cursor: 'pointer',
    }}
  >
    {label}
  </button>
);

/**
 * 줄바꿈 횟수 제한 검증. Textarea 가 ref 기반 비제어 컴포넌트라 onChange 로 잘라내면
 * DOM 과 formik 값이 어긋난다 → 검증으로 막는다(붙여넣기까지 걸린다).
 */
const validateNewlineMax = (max: number) => (value?: string | null) =>
  countNewlines(value) > max ? `줄바꿈은 최대 ${max}회까지 가능합니다` : undefined;

const newlineCaption = (max: number) => `* 줄바꿈 최대 ${max}회`;

/** 변수 입력 규칙은 전 유형 공통이라 폼 상단에 한 번만 안내한다. */
const VariableRuleNotice = () => (
  <View p={2} border={'1px solid'} borderColor={'border.muted'} borderRadius={2} backgroundColor={'canvas.subtle'}>
    <Text display={'block'} mb={1} fontSize={1} fontWeight={'bold'}>
      변수 입력 규칙
    </Text>
    {BRAND_MESSAGE_VARIABLE_RULES.map((rule) => (
      <Text key={rule} display={'block'} fontSize={0} color={'fg.muted'}>
        · {rule}
      </Text>
    ))}
  </View>
);

const Section = ({ title, children }: React.PropsWithChildren<{ title: string }>) => (
  <View p={3} border={'1px solid'} borderColor={'border.default'} borderRadius={2}>
    <Text display={'block'} mb={3} fontSize={1} fontWeight={'bold'}>
      {title}
    </Text>
    <FormLayout>{children}</FormLayout>
  </View>
);

// 웹링크는 PC/모바일을 따로 받지 않고 하나로 입력받아 양쪽에 같은 값을 넣는다.
//   따로 받으면 한쪽만 채워지기 쉽고(실제로 dev 저장분 다수가 linkPc=null), 그러면 PC 카카오톡에서
//   링크가 걸리지 않는다. 메시지 플래너(brand_mapper._links)와 동일한 규칙.
const LinkFields = ({ name, required = false }: { name: string; required?: boolean }) => {
  const { setFieldValue } = useFormikContext<FormValues>();
  return (
    <TextField
      label={'링크'}
      name={`${name}.linkMobile`}
      required={required}
      size={'large'}
      placeholder={'https://'}
      caption={'* 모바일·PC에 같은 링크로 등록됩니다'}
      onChange={(event) => {
        const nextValue = (event.target as HTMLInputElement).value;
        setFieldValue(`${name}.linkMobile`, nextValue);
        setFieldValue(`${name}.linkPc`, nextValue);
      }}
    />
  );
};

const toPriceNumber = (value: any) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * 가격은 카카오 규격상 아래 조합으로만 등록할 수 있다.
 *   1) 정상가격·할인 후 가격·할인율·정액할인가격 전부 고정변수명
 *   2) 정상가격(숫자)만
 *   3) 정상가격 + 할인 후 가격 + (할인율 | 정액할인가격) — 셋 다 숫자
 * 그래서 네 칸을 자유 입력으로 두지 않고, 할인 사용 여부(체크박스) → 할인율/정액할인 택1(라디오)
 * 흐름으로 좁히고 선택하지 않은 쪽은 비워 보낸다. (메시지 플래너와 동일한 방식)
 */
const CommerceFields = ({ name, titleMax = 30 }: { name: string; titleMax?: number }) => {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const commerce = (getIn(values, name) ?? {}) as FormValues;
  const regularPrice = toPriceNumber(commerce.regularPrice);
  const discountPrice = toPriceNumber(commerce.discountPrice);

  const usePriceVariable = Boolean(commerce.usePriceVariable);
  const [useDiscount, setUseDiscount] = React.useState(discountPrice != null);
  const [discountMode, setDiscountMode] = React.useState<'RATE' | 'FIXED'>(
    toPriceNumber(commerce.discountFixed) != null ? 'FIXED' : 'RATE',
  );

  const discountValid = regularPrice != null && discountPrice != null && regularPrice > 0 && discountPrice < regularPrice;
  const computedRate = discountValid ? Math.round(((regularPrice - discountPrice) / regularPrice) * 100) : null;
  const computedFixed = discountValid ? regularPrice - discountPrice : null;

  // 할인가를 입력하면 할인율/정액할인액을 자동 계산해 라디오로 고른 쪽만 채운다(반대쪽은 비움).
  //   고정변수명 모드에서는 네 값을 서버가 채우므로 숫자 필드를 전부 비운다.
  React.useEffect(() => {
    if (usePriceVariable) {
      setFieldValue(`${name}.regularPrice`, null);
      setFieldValue(`${name}.discountPrice`, null);
      setFieldValue(`${name}.discountRate`, null);
      setFieldValue(`${name}.discountFixed`, null);
      return;
    }
    if (!useDiscount) {
      setFieldValue(`${name}.discountPrice`, null);
      setFieldValue(`${name}.discountRate`, null);
      setFieldValue(`${name}.discountFixed`, null);
      return;
    }
    setFieldValue(`${name}.discountRate`, discountMode === 'RATE' ? computedRate : null);
    setFieldValue(`${name}.discountFixed`, discountMode === 'FIXED' ? computedFixed : null);
  }, [usePriceVariable, useDiscount, discountMode, computedRate, computedFixed, name, setFieldValue]);

  return (
    <View>
      <TextField
        label={'상품명'}
        name={`${name}.title`}
        required
        maxLength={titleMax}
        size={'large'}
        placeholder={'상품명 입력'}
      />
      <View mt={2}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <Checkbox
            checked={usePriceVariable}
            onChange={(event) => setFieldValue(`${name}.usePriceVariable`, event.target.checked)}
          />
          <Text fontSize={1}>고정변수명 사용</Text>
        </label>
      </View>
      {usePriceVariable ? (
        <View mt={2}>
          <Grid gapX={2} gapY={2}>
            {COMMERCE_PRICE_VARIABLE_FIELDS.map(({ label, variable }) => (
              <Grid.Unit key={variable} size={1 / 2}>
                <Text display={'block'} mb={1} fontSize={0} fontWeight={'bold'} color={'fg.muted'}>
                  {label}
                </Text>
                <View
                  p={2}
                  border={'1px solid'}
                  borderColor={'border.muted'}
                  borderRadius={2}
                  backgroundColor={'canvas.subtle'}
                >
                  <Text fontSize={1} color={'fg.muted'}>
                    {variable}
                  </Text>
                </View>
              </Grid.Unit>
            ))}
          </Grid>
          <Text display={'block'} mt={1} fontSize={0} color={'fg.muted'}>
            * 네 가격이 모두 고정변수명으로 등록됩니다. 숫자와 섞어 쓸 수 없습니다.
            <br />* 실제 노출 조합은 발송 쿼리에 넣은 컬럼이 정합니다 (정상가격만 / 정상가격+할인가격+할인율 /
            정상가격+할인가격+정액할인가격).
          </Text>
        </View>
      ) : null}
      {!usePriceVariable ? (
        <>
          <Grid gapX={2} gapY={2} sx={{ marginTop: 2 }}>
            <Grid.Unit size={1 / 2}>
              <NumberField label={'정상 가격(원)'} name={`${name}.regularPrice`} size={'large'} required />
            </Grid.Unit>
          </Grid>
          <View mt={2}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <Checkbox checked={useDiscount} onChange={(event) => setUseDiscount(event.target.checked)} />
              <Text fontSize={1}>할인가 사용</Text>
            </label>
          </View>
        </>
      ) : null}
      {!usePriceVariable && useDiscount ? (
        <View mt={2} p={2} border={'1px solid'} borderColor={'border.muted'} borderRadius={2}>
          <NumberField
            label={'할인 후 가격(원)'}
            name={`${name}.discountPrice`}
            size={'large'}
            required
            validate={(value) =>
              regularPrice != null && toPriceNumber(value) != null && toPriceNumber(value)! >= regularPrice
                ? '정상 가격보다 낮아야 합니다'
                : undefined
            }
          />
          <View mt={2}>
            <Text display={'block'} mb={1} fontSize={1} fontWeight={'bold'}>
              메시지에 넣을 할인 표기
            </Text>
            {(
              [
                { id: 'RATE', text: `할인율(%)${computedRate != null ? ` — ${computedRate}%` : ''}` },
                {
                  id: 'FIXED',
                  text: `정액할인가격(원)${computedFixed != null ? ` — ${computedFixed.toLocaleString()}원` : ''}`,
                },
              ] as const
            ).map((option) => (
              <label
                key={option.id}
                style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 4 }}
              >
                <input
                  type={'radio'}
                  name={`${name}-discount-mode`}
                  checked={discountMode === option.id}
                  onChange={() => setDiscountMode(option.id)}
                />
                <Text fontSize={1}>{option.text}</Text>
              </label>
            ))}
            <Text display={'block'} mt={1} fontSize={0} color={'fg.muted'}>
              {discountValid
                ? '* 정상가와 할인 후 가격으로 자동 계산됩니다'
                : '* 정상가보다 낮은 할인 후 가격을 입력하면 자동 계산됩니다'}
            </Text>
          </View>
        </View>
      ) : null}
    </View>
  );
};

const ButtonsField = ({
  name,
  values,
  setFieldValue,
  max = 2,
  min = 0,
  maxNameLength = 14,
  required = false,
}: {
  name: string;
  values: FormValues;
  setFieldValue: (field: string, value: any) => void;
  max?: number;
  min?: number;
  maxNameLength?: number;
  required?: boolean;
}) => {
  const buttons = (getIn(values, name) ?? []) as FormValues[];
  return (
    <FieldArray name={name}>
      {({ push, remove }) => (
        <View>
          <Text display={'block'} mb={2} fontSize={1} fontWeight={'bold'}>
            버튼{required ? ' (필수)' : ''}
          </Text>
          {buttons.map((button, index) => (
            <View key={index} mb={2} p={2} border={'1px solid'} borderColor={'border.muted'} borderRadius={2}>
              <Grid gapX={2} gapY={2} sx={{ alignItems: 'end' }}>
                <Grid.Unit size={1 / 3}>
                  <TextField
                    label={'버튼명'}
                    name={`${name}.${index}.name`}
                    required
                    maxLength={maxNameLength}
                    size={'large'}
                  />
                </Grid.Unit>
                <Grid.Unit size={1 / 3}>
                  <SelectField
                    label={'버튼 유형'}
                    name={`${name}.${index}.linkType`}
                    size={'large'}
                    options={[
                      // 실사용은 웹링크(WL)·앱링크(AL) 뿐이라 나머지는 노출하지 않는다.
                      //   (기존 데이터 확인: WL 129건 / AL 2건, 그 외 0건)
                      { id: 'WL', text: '웹 링크' },
                      { id: 'AL', text: '앱 링크' },
                    ]}
                    onChange={(linkType) => setFieldValue(`${name}.${index}`, { name: button.name, linkType })}
                  />
                </Grid.Unit>
                <Grid.Unit size={'min'}>
                  <IconButton
                    icon={XIcon}
                    onClick={() => remove(index)}
                    disabled={buttons.length <= min}
                    size={'large'}
                    aria-label={`${index + 1}번 버튼 삭제`}
                  />
                </Grid.Unit>
              </Grid>
              {button.linkType === 'WL' ? (
                <View mt={2}>
                  <LinkFields name={`${name}.${index}`} />
                </View>
              ) : button.linkType === 'AL' ? (
                <Grid gapX={2} gapY={2} sx={{ marginTop: 2 }}>
                  <Grid.Unit size={1 / 2}>
                    <TextField label={'Android 앱 링크'} name={`${name}.${index}.linkAndroid`} size={'large'} />
                  </Grid.Unit>
                  <Grid.Unit size={1 / 2}>
                    <TextField label={'iOS 앱 링크'} name={`${name}.${index}.linkIos`} size={'large'} />
                  </Grid.Unit>
                </Grid>
              ) : null}
            </View>
          ))}
          {buttons.length < max ? (
            <Button variant={'outline'} onClick={() => push(emptyButton())} leadingIcon={PlusIcon}>
              버튼 추가
            </Button>
          ) : null}
        </View>
      )}
    </FieldArray>
  );
};

const SingleMessageFields = ({
  type,
  values,
  setFieldValue,
}: {
  type: BrandMessageType;
  values: FormValues;
  setFieldValue: (field: string, value: any) => void;
}) => {
  const hasContent = ['TEXT', 'IMAGE', 'WIDE', 'PREMIUM_VIDEO'].includes(type);
  const hasImage = ['IMAGE', 'WIDE', 'COMMERCE'].includes(type);
  const limit = getBrandMessageLimit(type);
  const contentMax = limit.contentMax ?? 76;
  const contentNewlineMax = limit.contentNewlineMax;
  // 쿠폰을 넣으면 버튼 한 칸을 쿠폰이 차지한다(텍스트/이미지 5 → 4).
  const buttonMax = getButtonMax(type, Boolean(values.coupon));
  const buttonNameMax = limit.buttonNameMax;
  return (
    <>
      {type === 'PREMIUM_VIDEO' ? (
        <UploadField
          label={'동영상'}
          name={'video'}
          required={!values.videoId}
          accept={'video/*'}
          placeholder={values.videoId ? '기존 동영상을 사용합니다' : '동영상을 선택하세요'}
        />
      ) : null}
      {type === 'PREMIUM_VIDEO' ? (
        <TextField label={'헤더'} name={'header'} maxLength={20} size={'large'} placeholder={'헤더 입력(선택)'} />
      ) : null}
      {hasContent ? (
        <TextareaField
          label={'내용'}
          placeholder={'본문 입력'}
          name={'content'}
          required={type !== 'PREMIUM_VIDEO'}
          resize={'vertical'}
          maxLength={contentMax}
          validate={contentNewlineMax != null ? validateNewlineMax(contentNewlineMax) : undefined}
          caption={[`* 띄어쓰기 포함 최대 ${contentMax}자`, contentNewlineMax != null ? newlineCaption(contentNewlineMax) : null]
            .filter(Boolean)
            .join('\n')}
        />
      ) : null}
      {hasImage ? (
        <View>
          <BrandImageUploadField
            label={'이미지 첨부'}
            name={'image'}
            required={!values.imageUrl}
            specKey={type === 'WIDE' ? 'wide' : type === 'COMMERCE' ? 'commerce' : 'image'}
          />
          <TextField
            label={'이미지 링크'}
            name={'imageLink'}
            size={'large'}
            placeholder={'이미지 클릭 시 이동할 URL(선택)'}
            sx={{ marginTop: 2 }}
          />
        </View>
      ) : null}
      {type === 'COMMERCE' ? <CommerceFields name={'commerce'} /> : null}
      {type === 'COMMERCE' ? (
        <TextareaField
          label={'부가정보'}
          name={'additionalContent'}
          maxLength={34}
          resize={'none'}
          placeholder={'부가 정보 입력(선택)'}
          validate={validateNewlineMax(COMMERCE_ADDITIONAL_CONTENT_NEWLINE_MAX)}
          caption={newlineCaption(COMMERCE_ADDITIONAL_CONTENT_NEWLINE_MAX)}
        />
      ) : null}
      <ButtonsField
        name={'buttons'}
        values={values}
        setFieldValue={setFieldValue}
        max={buttonMax}
        min={type === 'COMMERCE' ? 1 : 0}
        maxNameLength={buttonNameMax}
        required={type === 'COMMERCE'}
      />
    </>
  );
};

const WideItemListFields = ({ values, setFieldValue }: { values: FormValues; setFieldValue: any }) => {
  const subItems = values.subWideItemList ?? [];
  const [activeTab, setActiveTab] = React.useState('main');

  React.useEffect(() => {
    if (activeTab === 'main') return;
    const activeIndex = Number(activeTab.replace('sub-', ''));
    if (activeIndex >= subItems.length) setActiveTab(`sub-${Math.max(0, subItems.length - 1)}`);
  }, [activeTab, subItems.length]);
  const { errors } = useFormikContext<FormValues>();

  const tabs: EditorTab[] = [
    { id: 'main', label: '메인', accent: true, error: hasTabError(errors, 'mainWideItem') },
    ...subItems.map((_: FormValues, index: number) => ({
      id: `sub-${index}`,
      label: `서브 ${index + 1}`,
      error: hasTabError(errors, `subWideItemList[${index}]`),
    })),
  ];

  return (
    <>
      <TextField label={'제목'} name={'header'} required maxLength={20} size={'large'} />
      <FieldArray name={'subWideItemList'}>
        {({ push, remove }) => (
          <View>
            <EditorTabs tabs={tabs} activeId={activeTab} onSelect={setActiveTab}>
              {subItems.length < 3 ? (
                <AddTabButton
                  label={'+ 서브 추가'}
                  onClick={() => {
                    push(emptyWideItem());
                    setActiveTab(`sub-${subItems.length}`);
                  }}
                />
              ) : null}
              {activeTab !== 'main' && subItems.length > 2 ? (
                <Button
                  variant={'danger'}
                  size={'small'}
                  onClick={() => {
                    const activeIndex = Number(activeTab.replace('sub-', ''));
                    remove(activeIndex);
                    setActiveTab(`sub-${Math.max(0, activeIndex - 1)}`);
                  }}
                  sx={{ marginLeft: 'auto', marginBottom: '4px' }}
                >
                  현재 탭 삭제
                </Button>
              ) : null}
            </EditorTabs>
            {activeTab === 'main' ? (
              // key=탭: 탭 전환 시 리마운트(UploadField 내부 file 상태가 이전 아이템 것으로 남는 것 방지)
              <Section key={activeTab} title={'메인 아이템'}>
                <TextField
                  label={'타이틀'}
                  name={'mainWideItem.title'}
                  maxLength={WIDE_ITEM_LIST_LIMITS.mainTitleMax}
                  size={'large'}
                  validate={validateNewlineMax(WIDE_ITEM_LIST_LIMITS.titleNewlineMax)}
                  caption={newlineCaption(WIDE_ITEM_LIST_LIMITS.titleNewlineMax)}
                />
                <BrandImageUploadField
                  label={'이미지 첨부'}
                  name={'mainWideItem.image'}
                  required={!values.mainWideItem?.imageUrl}
                  specKey={'listMain'}
                />
                <LinkFields name={'mainWideItem'} />
              </Section>
            ) : (
              (() => {
                const activeIndex = Number(activeTab.replace('sub-', ''));
                return (
                  <Section key={activeTab} title={`서브 ${activeIndex + 1} 아이템`}>
                    <TextField
                      label={'타이틀'}
                      name={`subWideItemList.${activeIndex}.title`}
                      required
                      maxLength={WIDE_ITEM_LIST_LIMITS.subTitleMax}
                      size={'large'}
                      validate={validateNewlineMax(WIDE_ITEM_LIST_LIMITS.titleNewlineMax)}
                      caption={newlineCaption(WIDE_ITEM_LIST_LIMITS.titleNewlineMax)}
                    />
                    <BrandImageUploadField
                      label={'이미지 첨부'}
                      name={`subWideItemList.${activeIndex}.image`}
                      required={!values.subWideItemList?.[activeIndex]?.imageUrl}
                      specKey={'listSub'}
                    />
                    <LinkFields name={`subWideItemList.${activeIndex}`} required />
                  </Section>
                );
              })()
            )}
          </View>
        )}
      </FieldArray>
      <ButtonsField
        name={'buttons'}
        values={values}
        setFieldValue={setFieldValue}
        max={BRAND_MESSAGE_LIMITS.WIDE_ITEM_LIST.buttonMax}
        maxNameLength={BRAND_MESSAGE_LIMITS.WIDE_ITEM_LIST.buttonNameMax}
      />
    </>
  );
};

const CarouselFields = ({
  type,
  values,
  setFieldValue,
}: {
  type: BrandMessageType;
  values: FormValues;
  setFieldValue: any;
}) => {
  const items = values.carousel?.list ?? [];
  const isCommerce = type === 'CAROUSEL_COMMERCE';
  const hasHead = Boolean(values.carousel?.head);
  const totalItems = items.length + (hasHead ? 1 : 0);
  const [activeTab, setActiveTab] = React.useState('item-0');

  React.useEffect(() => {
    if (activeTab === 'head') {
      if (!hasHead) setActiveTab('item-0');
      return;
    }
    const activeIndex = Number(activeTab.replace('item-', ''));
    if (activeIndex >= items.length) setActiveTab(`item-${Math.max(0, items.length - 1)}`);
  }, [activeTab, hasHead, items.length]);
  const { errors } = useFormikContext<FormValues>();

  const tabs: EditorTab[] = [
    ...(hasHead ? [{ id: 'head', label: '인트로', accent: true, error: hasTabError(errors, 'carousel.head') }] : []),
    ...items.map((_: FormValues, index: number) => ({
      id: `item-${index}`,
      label: `${index + 1}`,
      error: hasTabError(errors, `carousel.list[${index}]`),
    })),
  ];

  return (
    <>
      <FieldArray name={'carousel.list'}>
        {({ push, remove }) => (
          <View>
            <EditorTabs tabs={tabs} activeId={activeTab} onSelect={setActiveTab}>
              {totalItems < 6 ? (
                <AddTabButton
                  label={'+'}
                  onClick={() => {
                    push(emptyCarouselItem(type));
                    setActiveTab(`item-${items.length}`);
                  }}
                />
              ) : null}
              {isCommerce && !hasHead && totalItems < 6 ? (
                <AddTabButton
                  label={'+ 인트로'}
                  accent
                  onClick={() => {
                    setFieldValue('carousel.head', {
                      header: '',
                      content: '',
                      image: null,
                      imageUrl: null,
                      linkMobile: '',
                    });
                    setActiveTab('head');
                  }}
                />
              ) : null}
              {activeTab === 'head' ? (
                <Button
                  variant={'danger'}
                  size={'small'}
                  onClick={() => {
                    setFieldValue('carousel.head', null);
                    setActiveTab('item-0');
                  }}
                  sx={{ marginLeft: 'auto', marginBottom: '4px' }}
                >
                  현재 탭 삭제
                </Button>
              ) : items.length > 2 ? (
                <Button
                  variant={'danger'}
                  size={'small'}
                  onClick={() => {
                    const activeIndex = Number(activeTab.replace('item-', ''));
                    remove(activeIndex);
                    setActiveTab(`item-${Math.max(0, Math.min(activeIndex, items.length - 2))}`);
                  }}
                  sx={{ marginLeft: 'auto', marginBottom: '4px' }}
                >
                  현재 탭 삭제
                </Button>
              ) : null}
            </EditorTabs>
            {activeTab === 'head' && values.carousel?.head ? (
              // key=탭: 탭이 바뀌면 리마운트한다. 없으면 React 가 같은 위치의 Section 을 재사용해
              //   UploadField 내부 file 상태(“… 준비 완료”)가 이전 카드 것으로 남는다.
              <Section key={activeTab} title={'캐러셀 인트로'}>
                <TextField label={'제목'} name={'carousel.head.header'} maxLength={20} size={'large'} />
                <TextareaField label={'내용'} name={'carousel.head.content'} maxLength={50} resize={'none'} />
                <BrandImageUploadField
                  label={'이미지'}
                  name={'carousel.head.image'}
                  required={!values.carousel.head.imageUrl}
                  specKey={'commerce'}
                />
                <LinkFields name={'carousel.head'} />
              </Section>
            ) : (
              (() => {
                const activeIndex = Number(activeTab.replace('item-', ''));
                return (
                  <Section key={activeTab} title={`${activeIndex + 1}번째 카드`}>
                    {isCommerce ? (
                      <CommerceFields name={`carousel.list.${activeIndex}.commerce`} />
                    ) : (
                      <TextField
                        label={'제목'}
                        name={`carousel.list.${activeIndex}.header`}
                        required
                        maxLength={20}
                        size={'large'}
                      />
                    )}
                    {!isCommerce ? (
                      <TextareaField
                        label={'내용'}
                        name={`carousel.list.${activeIndex}.content`}
                        required
                        maxLength={CAROUSEL_ITEM_LIMITS.contentMax}
                        resize={'vertical'}
                        validate={validateNewlineMax(CAROUSEL_ITEM_LIMITS.contentNewlineMax)}
                        caption={`* 띄어쓰기 포함 최대 ${CAROUSEL_ITEM_LIMITS.contentMax}자\n${newlineCaption(
                          CAROUSEL_ITEM_LIMITS.contentNewlineMax,
                        )}`}
                      />
                    ) : (
                      <TextareaField
                        label={'부가정보'}
                        name={`carousel.list.${activeIndex}.additionalContent`}
                        maxLength={34}
                        resize={'none'}
                        placeholder={'부가 정보 입력(선택)'}
                        validate={validateNewlineMax(COMMERCE_ADDITIONAL_CONTENT_NEWLINE_MAX)}
                        caption={newlineCaption(COMMERCE_ADDITIONAL_CONTENT_NEWLINE_MAX)}
                      />
                    )}
                    <BrandImageUploadField
                      label={'이미지'}
                      name={`carousel.list.${activeIndex}.image`}
                      required={!values.carousel.list?.[activeIndex]?.imageUrl}
                      specKey={isCommerce ? 'commerce' : 'carousel'}
                    />
                    <TextField
                      label={'이미지 링크'}
                      name={`carousel.list.${activeIndex}.imageLink`}
                      size={'large'}
                      placeholder={'https://'}
                    />
                    <ButtonsField
                      name={`carousel.list.${activeIndex}.buttons`}
                      values={values}
                      setFieldValue={setFieldValue}
                      max={CAROUSEL_ITEM_LIMITS.buttonMax}
                      min={CAROUSEL_ITEM_LIMITS.buttonMin}
                      maxNameLength={CAROUSEL_ITEM_LIMITS.buttonNameMax}
                      required
                    />
                  </Section>
                );
              })()
            )}
          </View>
        )}
      </FieldArray>
      <Section title={'캐러셀 더보기'}>
        {values.carousel?.tail ? (
          <View>
            <LinkFields name={'carousel.tail'} />
            <Button variant={'invisible'} onClick={() => setFieldValue('carousel.tail', null)}>
              더보기 제거
            </Button>
          </View>
        ) : (
          <Button variant={'outline'} onClick={() => setFieldValue('carousel.tail', { linkMobile: '' })}>
            더보기 추가
          </Button>
        )}
      </Section>
    </>
  );
};

type BrandMessageTemplateFormProps = {
  values: FormValues;
  setFieldValue: (field: string, value: any) => void;
  handleSubmit: () => void;
  nonFieldErrorRef: React.RefObject<HTMLDivElement>;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  submitLabel: string;
  typeDisabled?: boolean;
};

export const BrandMessageTemplateForm = ({
  values,
  setFieldValue,
  handleSubmit,
  nonFieldErrorRef,
  scrollContainerRef,
  submitLabel,
  typeDisabled = false,
}: BrandMessageTemplateFormProps) => {
  const type = values.chatBubbleType as BrandMessageType;
  return (
    <>
      <Dialog.Body ref={scrollContainerRef}>
        <NonFieldError ref={nonFieldErrorRef} />
        <Grid gapX={4} gapY={4} wrap={[true, true, false]}>
          <Grid.Unit size={[1, 1, 3 / 5]}>
            <FormLayout>
              <TextField label={'템플릿명'} name={'templateName'} required autoComplete={'off'} size={'large'} />
              <SelectField
                label={'메시지 유형'}
                name={'chatBubbleType'}
                required
                disabled={typeDisabled}
                size={'large'}
                options={[...BRAND_MESSAGE_TYPE_OPTIONS]}
                onChange={(nextValue: string) => {
                  const nextType = nextValue as BrandMessageType;
                  const next = initialValuesForType(nextType);
                  setFieldValue('chatBubbleType', nextType);
                  // 유형 전용 구조(리스트 항목·캐러셀 카드·동영상)만 초기화하고
                  // 본문·헤더·이미지·버튼 같은 공통 값은 유지한다. 전부 지우면
                  // 복제에서 유형을 바꿀 때 원본 값이 날아가 복제할 이유가 없어진다.
                  (['mainWideItem', 'subWideItemList', 'carousel', 'video', 'videoId'] as const).forEach((name) =>
                    setFieldValue(name, next[name] ?? null),
                  );
                  // 새 유형이 안 쓰는 값은 비우고, 새로 필요한 값은 기본값으로 채운다.
                  setFieldValue('image', next.image === null ? null : (values.image ?? next.image));
                  setFieldValue('commerce', next.commerce === null ? null : (values.commerce ?? next.commerce));
                  if (next.buttons.length && !(values.buttons ?? []).length) setFieldValue('buttons', next.buttons);
                }}
              />
              <VariableRuleNotice />
              {/* key=유형: 유형을 바꾸면 리마운트한다. 없으면 UploadField 내부 file 상태가
                  이전 유형 것으로 남아 "… 준비 완료" 문구가 그대로 보인다. */}
              {type === 'WIDE_ITEM_LIST' ? (
                <WideItemListFields key={type} values={values} setFieldValue={setFieldValue} />
              ) : type === 'CAROUSEL_FEED' || type === 'CAROUSEL_COMMERCE' ? (
                <CarouselFields key={type} type={type} values={values} setFieldValue={setFieldValue} />
              ) : (
                <SingleMessageFields key={type} type={type} values={values} setFieldValue={setFieldValue} />
              )}
            </FormLayout>
          </Grid.Unit>
          <Grid.Unit size={[1, 1, 2 / 5]}>
            <View sx={{ position: ['static', 'static', 'sticky'], top: 0 }}>
              <BrandMessageTemplatePreview value={values} />
            </View>
          </Grid.Unit>
        </Grid>
      </Dialog.Body>
      <Dialog.Footer>
        <Button variant={'primary'} size={'large'} onClick={() => handleSubmit()}>
          {submitLabel}
        </Button>
      </Dialog.Footer>
    </>
  );
};

const BrandMessageTemplateCreateDialog = ({ config, initialValues = {}, ...props }: Props) => (
  <Dialog {...props} wide>
    <Dialog.Header>
      <Text fontSize={2} fontWeight={'bold'}>
        브랜드 메시지 템플릿 제작하기
      </Text>
    </Dialog.Header>
    <MutationFormik<BrandMessageTemplateCreateDialog_brandMessageTemplateCreateMutation>
      mutation={graphql`
        mutation BrandMessageTemplateCreateDialog_brandMessageTemplateCreateMutation(
          $input: BrandMessageTemplateCreateInput!
        ) {
          brandMessageTemplateCreate(input: $input) {
            id
          }
        }
      `}
      initialValues={{
        templateName: '',
        chatBubbleType: 'WIDE',
        adult: false,
        ...initialValuesForType('WIDE'),
        ...initialValues,
      }}
      validate={validateBrandMessageTemplate}
      valuesTransformer={(values) => normalizeBrandMessageTemplateInput(values)}
      config={config}
    >
      {({ handleSubmit, values, setFieldValue }, { nonFieldErrorRef, scrollContainerRef }) => (
        <BrandMessageTemplateForm
          values={values}
          setFieldValue={setFieldValue}
          handleSubmit={handleSubmit}
          nonFieldErrorRef={nonFieldErrorRef}
          scrollContainerRef={scrollContainerRef}
          submitLabel={'제작하기'}
        />
      )}
    </MutationFormik>
  </Dialog>
);

export default BrandMessageTemplateCreateDialog;
